import React, { useState, useEffect } from "react";
import { Tournament } from "../types/tournament";
import { QrCode, Smartphone, Share2 } from "lucide-react";

interface QRCodeDisplayProps {
  tournament: Tournament;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({ tournament }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    // Generate QR code URL for the tournament
    const baseUrl = window.location.origin;
    const tournamentUrl = `${baseUrl}/tournament/${tournament.id}/standings`;
    setQrCodeUrl(tournamentUrl);
  }, [tournament.id]);

  const generateQRCode = (text: string) => {
    // Simple QR code generation using a service
    const qrServiceUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
      text
    )}`;
    return qrServiceUrl;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(qrCodeUrl);
      alert("Tournament URL copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="text-center">
        <QrCode className="w-12 h-12 text-primary-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Live Standings Access
        </h3>
        <p className="text-gray-600 mb-6">
          Scan this QR code with your phone to view live tournament standings
        </p>

        <div className="space-y-4">
          {/* QR Code Display */}
          <div className="flex justify-center">
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
              {showQR ? (
                <img
                  src={generateQRCode(qrCodeUrl)}
                  alt="QR Code for tournament standings"
                  className="w-48 h-48"
                />
              ) : (
                <div className="w-48 h-48 flex items-center justify-center">
                  <button
                    onClick={() => setShowQR(true)}
                    className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 flex items-center"
                  >
                    <QrCode className="w-5 h-5 mr-2" />
                    Generate QR Code
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
              <Smartphone className="w-4 h-4 mr-2" />
              How to Use:
            </h4>
            <ol className="text-sm text-blue-800 space-y-1 text-left">
              <li>1. Open your phone's camera app</li>
              <li>2. Point it at the QR code above</li>
              <li>3. Tap the notification to open standings</li>
              <li>4. View live tournament updates!</li>
            </ol>
          </div>

          {/* Direct Link */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
              <Share2 className="w-4 h-4 mr-2" />
              Direct Link:
            </h4>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={qrCodeUrl}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
              />
              <button
                onClick={copyToClipboard}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 text-sm"
              >
                Copy
              </button>
            </div>
          </div>

          {/* Tournament Info */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">
              Tournament Info
            </h4>
            <div className="text-sm text-green-800 space-y-1">
              <div>
                <strong>Name:</strong> {tournament.name}
              </div>
              <div>
                <strong>Round:</strong> {tournament.currentRound} of{" "}
                {tournament.totalRounds}
              </div>
              <div>
                <strong>Teams:</strong> {tournament.teams.length}
              </div>
              <div>
                <strong>Courts:</strong> {tournament.courts.length}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeDisplay;
